<template>
  <div class="flex flex-col gap-8">
    <section class="flex flex-col gap-5">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-baseline gap-3">
          <h2 class="text-xl font-semibold text-text-primary">{{ $t('play.matchmaking.your_party') }}</h2>
          <span
            v-if="party && partyLimit"
            class="text-sm tabular-nums"
            :class="partyTooBig ? 'text-red-400' : 'text-text-secondary'"
          >{{ $t('play.matchmaking.party_count', { n: partySize, max: partyLimit }) }}</span>
        </div>
        <div class="flex items-center gap-2">
          <button
            @click="copyLobbyLink"
            :disabled="!party?.uuid"
            class="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-text-secondary hover:text-text-primary hover:bg-gray-900 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            <LinkIcon class="w-4 h-4" />
            {{ linkCopied ? $t('play.matchmaking.copied') : $t('play.matchmaking.copy_invite_link') }}
          </button>
        </div>
      </div>

      <p v-if="!party" class="text-sm text-text-secondary">{{ $t('common.loading') }}</p>
      <ul
        v-else
        class="grid grid-cols-2 sm:grid-cols-[repeat(4,minmax(0,13rem))] gap-4"
        :class="wideLineup ? 'xl:grid-cols-[repeat(var(--seats),minmax(0,1fr))] xl:gap-3' : ''"
        :style="{ '--seats': seatCount }"
      >
        <li
          v-for="member in party.members"
          :key="member.id"
          class="group relative h-40 rounded-2xl flex flex-col items-center justify-center gap-3 px-3 text-center"
          :class="member.id === party.owner_id ? 'seat-leader ring-1 ring-primary-700' : 'seat-member'"
        >
          <div class="relative">
            <img
              v-if="member.avatarfull || member.avatar"
              :src="member.avatarfull || member.avatar"
              alt=""
              class="rounded-full object-cover ring-2"
              :class="[avatarSize, member.id === party.owner_id ? 'ring-primary-500' : 'ring-gray-700']"
            />
            <img
              v-else
              src="../../assets/logos/refrag-avatar.svg"
              alt=""
              class="rounded-full object-cover ring-2"
              :class="[avatarSize, member.id === party.owner_id ? 'ring-primary-500' : 'ring-gray-700']"
            />
            <span
              class="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full ring-4"
              :class="[
                member.online ? 'bg-green-500' : 'bg-gray-600',
                member.id === party.owner_id ? 'ring-primary-950' : 'ring-gray-900',
              ]"
              :title="member.online ? $t('play.matchmaking.online') : $t('play.matchmaking.offline')"
            />
          </div>
          <div class="w-full min-w-0">
            <p class="font-semibold text-base text-text-primary truncate">{{ member.username }}</p>
            <p v-if="member.id === party.owner_id" class="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary-300">
              <StarIcon class="w-3 h-3" aria-hidden="true" />
              {{ $t('play.matchmaking.party_leader') }}
            </p>
            <p v-else class="mt-1 text-xs text-text-secondary">
              {{ member.id === auth.user()?.id ? $t('play.matchmaking.you') : (member.online ? $t('play.matchmaking.online') : $t('play.matchmaking.offline')) }}
            </p>
          </div>
          <button
            v-if="isOwner && member.id !== party.owner_id"
            @click="kickTarget = member"
            class="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-gray-500 opacity-60 group-hover:opacity-100 hover:bg-red-600 hover:text-white transition focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            :title="$t('play.matchmaking.kick_title')"
            :aria-label="$t('play.matchmaking.kick_title')"
          >
            <XMarkIcon class="w-4 h-4" />
          </button>
        </li>

        <li v-for="seat in openSeats" :key="`seat-${seat}`">
          <button
            @click="$emit('invite')"
            class="w-full h-40 rounded-2xl border-2 border-dashed border-gray-700 flex flex-col items-center justify-center gap-3 text-gray-500 hover:border-primary-500 hover:text-primary-300 hover:bg-primary-950/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            <span class="rounded-full border-2 border-dashed border-current flex items-center justify-center" :class="avatarSize">
              <PlusIcon class="w-6 h-6" />
            </span>
            <span class="text-sm font-semibold">{{ $t('play.matchmaking.invite') }}</span>
          </button>
        </li>
      </ul>
    </section>

    <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
      <div class="relative group">
        <PlaylistDropdown v-model="shownPlayMode" :play-modes="playModes" :disabled="selectorsLocked" />
        <QueueLockHint v-if="selectorsLocked" :reason="lockedReason" />
      </div>
      <div class="relative group">
        <MapsDropdown v-model="shownMapNames" :cs-maps="shownMaps" :disabled="selectorsLocked" />
        <QueueLockHint v-if="selectorsLocked" :reason="lockedReason" />
      </div>
      <div class="relative group">
        <RegionDropdown v-model="shownRegion" :regions="regions" :disabled="selectorsLocked" />
        <QueueLockHint v-if="selectorsLocked" :reason="lockedReason" />
      </div>
    </div>

    <section class="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
      <div class="flex-1 min-w-0 flex items-start gap-3">
        <span v-if="isQueued" class="relative flex w-3 h-3 mt-2 flex-shrink-0" aria-hidden="true">
          <span class="absolute inline-flex w-full h-full rounded-full bg-primary-400 opacity-75 motion-safe:animate-ping" />
          <span class="relative inline-flex w-3 h-3 rounded-full bg-primary-500" />
        </span>
        <div class="min-w-0">
          <p class="text-lg sm:text-xl font-semibold text-text-primary">{{ summaryLabel }}</p>
          <div class="flex items-center gap-1.5 mt-1">
            <p class="text-sm" :class="statusIsProblem ? 'text-red-400' : 'text-text-secondary'" aria-live="polite">
              {{ statusLabel }}
            </p>
            <button
              v-if="isQueued"
              type="button"
              class="p-1 rounded-md text-text-secondary hover:text-text-primary hover:bg-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              :aria-pressed="!searchingSoundOn"
              :aria-label="searchingSoundOn ? $t('play.matchmaking.searching_sound_off') : $t('play.matchmaking.searching_sound_on')"
              :title="searchingSoundOn ? $t('play.matchmaking.searching_sound_off') : $t('play.matchmaking.searching_sound_on')"
              @click="toggleSearchingSound"
            >
              <SpeakerWaveIcon v-if="searchingSoundOn" class="w-4 h-4" aria-hidden="true" />
              <SpeakerXMarkIcon v-else class="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3 flex-shrink-0">
        <button
          v-if="!isOwner && party"
          @click="leaveParty"
          :disabled="leaving"
          class="px-4 py-3 rounded-lg text-base font-semibold text-text-secondary hover:text-text-primary hover:bg-gray-900 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        >
          {{ leaving ? $t('play.matchmaking.leaving') : $t('play.matchmaking.leave_party') }}
        </button>

        <router-link
          v-if="activeLobby"
          :to="{ name: 'MatchmakingLobbyPage', params: { id: activeLobby.uuid } }"
          class="px-8 py-4 rounded-lg bg-green-700 text-white text-lg font-semibold hover:bg-green-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
        >
          {{ $t('play.matchmaking.go_to_matchroom') }}
        </router-link>
        <template v-else-if="isOwner">
          <button
            v-if="isQueued"
            @click="cancelMatchmaking"
            class="px-8 py-4 rounded-lg bg-gray-800 text-text-primary text-lg font-semibold hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            {{ $t('play.matchmaking.cancel_matchmaking') }}
          </button>
          <button
            v-else
            :disabled="!canBeginMatchmaking || queueing"
            @click="beginMatchmaking"
            class="px-8 py-4 rounded-lg bg-primary-600 text-white text-lg font-semibold shadow-[0_0_32px_rgba(78,84,200,0.35)] hover:bg-primary-500 transition-colors disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          >
            {{ $t('play.matchmaking.begin') }}
          </button>
        </template>
      </div>
    </section>

    <div v-if="kickTarget" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div class="bg-gray-900 border border-gray-700 rounded-xl p-6 w-80 text-center shadow-xl">
        <h2 class="text-text-primary font-semibold text-lg mb-2">{{ $t('play.matchmaking.kick_prompt') }}</h2>
        <i18n-t scope="global" keypath="play.matchmaking.kick_body" tag="p" class="text-text-secondary text-sm mb-6">
          <template #name><span class="text-text-primary font-medium">{{ kickTarget.username }}</span></template>
        </i18n-t>
        <div class="flex gap-3">
          <button
            @click="kickTarget = null"
            class="flex-1 px-4 py-2 rounded-lg border border-gray-700 text-text-secondary text-sm hover:bg-gray-800 transition-colors"
          >{{ $t('common.cancel') }}</button>
          <button
            @click="confirmKick"
            class="flex-1 px-4 py-2 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
          >{{ $t('common.remove') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import {
  LinkIcon, PlusIcon, SpeakerWaveIcon, SpeakerXMarkIcon, StarIcon, XMarkIcon,
} from '@heroicons/vue/24/solid';
import PlaylistDropdown from './PlaylistDropdown.vue';
import MapsDropdown from './MapsDropdown.vue';
import RegionDropdown from './RegionDropdown.vue';
import QueueLockHint from './QueueLockHint.vue';
import { useMatchmakingQueue } from '../../composables/useMatchmakingQueue';
import useAudioSettings from '../../composables/useAudioSettings';

defineEmits(['invite']);

const auth = useAuth();

const {
  playModes,
  regions,
  party,
  activeLobby,
  isQueued,
  isOwner,
  queueing,
  leaving,
  selectorsLocked,
  lockedReason,
  shownPlayMode,
  shownRegion,
  shownMapNames,
  shownMaps,
  partySize,
  partyLimit,
  partyTooBig,
  canBeginMatchmaking,
  summaryLabel,
  statusIsProblem,
  statusLabel,
  beginMatchmaking,
  cancelMatchmaking,
  leaveParty,
} = useMatchmakingQueue();

const { settings: audioSettings, updateSound } = useAudioSettings();

const kickTarget = ref(null);
const linkCopied = ref(false);

const searchingSoundOn = computed(() => !audioSettings.value.sounds.searching.muted);
const openSeats = computed(() => Math.max(0, partyLimit.value - partySize.value));
const seatCount = computed(() => partySize.value + openSeats.value);
const wideLineup = computed(() => seatCount.value > 4);
const avatarSize = computed(() => (wideLineup.value ? 'w-16 h-16 xl:w-14 xl:h-14' : 'w-16 h-16'));

// Turning it on while queued starts the loop again: the sounds follow the setting.
function toggleSearchingSound() {
  updateSound('searching', { muted: searchingSoundOn.value });
}

function copyLobbyLink() {
  if (!party.value?.uuid) return;
  const url = `${window.location.origin}/matchmaking?join=${party.value.uuid}`;
  navigator.clipboard.writeText(url).then(() => {
    linkCopied.value = true;
    setTimeout(() => { linkCopied.value = false; }, 2000);
  });
}

function confirmKick() {
  const memberId = kickTarget.value?.member_id;
  kickTarget.value = null;
  if (!memberId) return;
  axios.delete(`/matchmaking/party_members/${memberId}`).catch(() => {});
}
</script>

<style scoped>
.seat-leader {
  background: radial-gradient(90% 65% at 50% 0%, rgba(78, 84, 200, 0.4) 0%, rgba(78, 84, 200, 0) 100%), #08091b;
}

.seat-member {
  background: radial-gradient(90% 65% at 50% 0%, rgba(124, 147, 177, 0.16) 0%, rgba(124, 147, 177, 0) 100%), #0f141a;
}
</style>
